import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  Download,
  ExternalLink,
  Link2,
  Loader,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Sheet,
  Trash2,
} from 'lucide-react';
import { GlassButton, GlassModal } from '../ui';
import { crmImportApi, CrmImportJob } from '../../services/crmImportApi';
import { getCrmWritableProjects, Project } from '../../services/api';
import {
  gsheetImportApi,
  GsheetImportJob,
  GsheetSource,
  GsheetSourceStatus,
  GsheetStatus,
} from '../../services/gsheetImportApi';

// Saved syncing Google Sheet sources (google-sheets-lead-import Phase 3a).
// Backend: GET/PATCH/DELETE /api/crm-import/gsheets/sources[/{id}], plus
// /resume, /retry-failed and /remap. Phase 3b adds "Sync now" and the interval
// select; neither is rendered here yet.

export type OpenJobRequest = {
  job: GsheetImportJob | CrmImportJob;
  step: 'configure' | 'progress';
  source?: GsheetSource | null;
};

interface Props {
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
  projectId?: string | null;
  refreshKey?: number;
  onOpenJob: (req: OpenJobRequest) => void;
}

const STATUS_LABEL: Record<GsheetSourceStatus, string> = {
  active: 'Syncing',
  paused: 'Paused (plan)',
  needs_reauth: 'Reconnect Google',
  needs_remap: 'Needs re-map',
  rekey_suspected: 'Check key column',
  source_lost: 'Sheet unavailable',
  error: 'Imports failing',
};

const RESUMABLE: GsheetSourceStatus[] = ['paused', 'error', 'source_lost', 'rekey_suspected', 'needs_reauth'];

function intervalLabel(minutes: number): string {
  if (minutes >= 1440) return 'Daily';
  if (minutes === 60) return 'Every hour';
  if (minutes > 60 && minutes % 60 === 0) return `Every ${minutes / 60} hours`;
  return `Every ${minutes} min`;
}

// Backend timestamps are naive UTC ISO strings (no offset) — parse as UTC.
function parseUtc(iso: string | null): Date | null {
  if (!iso) return null;
  const hasZone = /[zZ]|[+-]\d\d:?\d\d$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function relative(iso: string | null, future = false): string {
  const d = parseUtc(iso);
  if (!d) return future ? 'soon' : 'never';
  const diff = Math.round((d.getTime() - Date.now()) / 60000);
  const abs = Math.abs(diff);
  const unit = abs < 60 ? `${abs} min` : abs < 1440 ? `${Math.round(abs / 60)} h` : `${Math.round(abs / 1440)} d`;
  if (abs < 1) return 'just now';
  return diff > 0 ? `in ${unit}` : `${unit} ago`;
}

function apiError(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (typeof d === 'object' && typeof d.detail === 'string') return d.detail;
  return fallback;
}

const GsheetLeadSourcesList: React.FC<Props> = ({ onToast, refreshKey = 0, onOpenJob }) => {
  const [sources, setSources] = useState<GsheetSource[] | null>(null);
  const [google, setGoogle] = useState<GsheetStatus | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmStop, setConfirmStop] = useState<GsheetSource | null>(null);
  const [confirmNew, setConfirmNew] = useState<GsheetSource | null>(null);
  // crm-per-project-scoping: choose a project for a source that has none.
  const [projectOptions, setProjectOptions] = useState<Project[]>([]);
  const [choosingFor, setChoosingFor] = useState<number | null>(null);
  const [chosenProject, setChosenProject] = useState<string>('');
  useEffect(() => {
    getCrmWritableProjects().then(setProjectOptions).catch(() => setProjectOptions([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const [{ data }, status] = await Promise.all([
        gsheetImportApi.listSources(),
        gsheetImportApi.status().then((r) => r.data).catch(() => null),
      ]);
      setSources(data);
      setGoogle(status);
    } catch {
      setSources([]);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (src: GsheetSource, fn: () => Promise<any>, ok?: string) => {
    setBusyId(src.id);
    try {
      await fn();
      if (ok) onToast(ok, 'success');
    } catch (e: any) {
      onToast(apiError(e, 'That did not work — please try again'), 'error');
    } finally {
      setBusyId(null);
      load();
    }
  };

  const resume = (src: GsheetSource, confirmNewRows = false) =>
    act(src, () => gsheetImportApi.resume(src.id, confirmNewRows), 'Syncing resumed');

  const remap = async (src: GsheetSource) => {
    setBusyId(src.id);
    try {
      const { data } = await gsheetImportApi.remap(src.id);
      onOpenJob({ job: data, step: 'configure', source: src });
      onToast('Re-map the columns above, then start the import', 'info');
    } catch (e: any) {
      onToast(apiError(e, "Couldn't re-fetch the sheet"), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const openLatestJob = async (src: GsheetSource) => {
    if (!src.last_job_id) return;
    try {
      const { data } = await crmImportApi.getJob(src.last_job_id);
      onOpenJob({ job: data, step: 'progress', source: src });
    } catch {
      onToast("Couldn't open the latest import", 'error');
    }
  };

  const downloadErrors = async (src: GsheetSource) => {
    if (!src.last_job_id) return;
    try {
      const { data } = await crmImportApi.downloadErrors(src.last_job_id);
      const blob = new Blob([data as any], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_import_${src.last_job_id}_errors.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      onToast('Could not download the error report', 'error');
    }
  };

  const reconnect = async () => {
    try {
      const { data } = await gsheetImportApi.authStart();
      window.location.href = data.auth_url;
    } catch (e: any) {
      onToast(apiError(e, "Couldn't start the Google sign-in"), 'error');
    }
  };

  if (sources === null) {
    return (
      <div className="gsheet-sources">
        <p className="crm-import-hint"><Loader size={14} className="crm-spin" /> Loading syncing sheets…</p>
      </div>
    );
  }
  if (sources.length === 0) return null;

  const googleConnected = google?.status === 'connected';

  return (
    <div className="gsheet-sources">
      <h3 className="gsheet-sources__title"><RefreshCw size={16} /> Syncing sheets</h3>
      {sources.map((src) => {
        const busy = busyId === src.id;
        const title = `${src.spreadsheet_title || 'Google Sheet'} — ${src.sheet_title || 'tab'}`;
        const canResume = RESUMABLE.includes(src.status) && (src.status !== 'needs_reauth' || googleConnected);
        const problemRows = src.rows_failed_total + src.rows_invalid;
        return (
          <div key={src.id} className="gsheet-source-row">
            <div className="gsheet-source-row__meta">
              <div className="gsheet-source-row__head">
                <Sheet size={16} />
                <span className="gsheet-source-row__name" title={title}>{title}</span>
                <span className={`gsheet-status-badge gsheet-status-badge--${src.status}`}>
                  {STATUS_LABEL[src.status] || src.status}
                </span>
                {!src.sync_enabled && <span className="gsheet-status-badge gsheet-status-badge--paused">Paused by you</span>}
              </div>
              <div className="gsheet-source-row__facts">
                <span>{src.target_module}</span>
                {src.campaign_name && <span>Campaign: {src.campaign_name}</span>}
                <span>{intervalLabel(src.sync_interval_minutes)}</span>
                <span>Last synced {relative(src.last_synced_at)}</span>
                <span>{src.rows_imported_total} imported</span>
              </div>
              {!src.project_id && (
                <div className="gsheet-source-row__note gsheet-source-row__note--warn" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  <span className="gsheet-status-badge gsheet-status-badge--paused">
                    Unassigned project — new leads go to your Unassigned CRM records
                  </span>
                  {choosingFor === src.id ? (
                    <>
                      <select
                        className="crm-import-select"
                        value={chosenProject}
                        aria-label="Project"
                        onChange={(e) => setChosenProject(e.target.value)}
                      >
                        <option value="">— Choose a project —</option>
                        {projectOptions.map((p) => (
                          <option key={p.project_id} value={p.project_id}>{p.name}</option>
                        ))}
                      </select>
                      <GlassButton
                        variant="primary"
                        size="sm"
                        disabled={busy || !chosenProject}
                        onClick={() => {
                          const pid = chosenProject;
                          setChoosingFor(null);
                          setChosenProject('');
                          act(src, () => gsheetImportApi.setProject(src.id, pid), 'Project set');
                        }}
                      >
                        Save
                      </GlassButton>
                      <GlassButton variant="secondary" size="sm" onClick={() => { setChoosingFor(null); setChosenProject(''); }}>
                        Cancel
                      </GlassButton>
                    </>
                  ) : (
                    <GlassButton variant="secondary" size="sm" disabled={busy} onClick={() => { setChoosingFor(src.id); setChosenProject(''); }}>
                      Choose project
                    </GlassButton>
                  )}
                </div>
              )}
              {src.status_reason && src.status !== 'active' && (
                <p className="gsheet-source-row__note gsheet-source-row__note--warn">
                  <AlertTriangle size={14} /> {src.status_reason}
                </p>
              )}
              {src.last_failure_reason && (
                <p className="gsheet-source-row__note gsheet-source-row__note--warn">
                  <AlertTriangle size={14} /> Last import failed: {src.last_failure_reason}
                  {src.status === 'active' ? ` — retrying ${relative(src.next_sync_at, true)}` : ''}
                </p>
              )}
              {src.rows_pending_retry > 0 && (
                <p className="gsheet-source-row__note">{src.rows_pending_retry} rows waiting to retry</p>
              )}
              {src.rows_failed_total > 0 && (
                <p className="gsheet-source-row__note gsheet-source-row__note--warn">
                  {src.rows_failed_total} rows could not be imported
                </p>
              )}
              {src.rows_invalid > 0 && (
                <p className="gsheet-source-row__note gsheet-source-row__note--warn">
                  {src.rows_invalid} rows have an invalid email and were skipped — fix the cell in the Sheet, then Retry
                </p>
              )}
              {(src.rows_pending_retry > 0 || problemRows > 0) && src.last_job_id && (
                <button type="button" className="gsheet-source-row__link" onClick={() => downloadErrors(src)}>
                  <Download size={13} /> Latest import's error report
                </button>
              )}
              {src.key_conflict_rows > 0 && (
                <p className="gsheet-source-row__note gsheet-source-row__note--warn">
                  {src.key_conflict_rows} rows share a key with an earlier row and were not imported — Re-map and pick
                  a different key column
                </p>
              )}
            </div>
            <div className="gsheet-source-row__actions">
              {src.sync_enabled ? (
                <GlassButton variant="secondary" size="sm" disabled={busy}
                  onClick={() => act(src, () => gsheetImportApi.setSyncEnabled(src.id, false), 'Syncing paused')}>
                  <Pause size={14} /> Pause
                </GlassButton>
              ) : (
                <GlassButton variant="secondary" size="sm" disabled={busy}
                  onClick={() => act(src, () => gsheetImportApi.setSyncEnabled(src.id, true), 'Syncing unpaused')}>
                  <Play size={14} /> Unpause
                </GlassButton>
              )}
              {canResume && (
                <GlassButton variant="primary" size="sm" disabled={busy} onClick={() => resume(src)}>
                  {busy ? <Loader size={14} className="crm-spin" /> : <RotateCcw size={14} />} Resume
                </GlassButton>
              )}
              {src.status === 'rekey_suspected' && (
                <GlassButton variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmNew(src)}>
                  These are new rows
                </GlassButton>
              )}
              {src.status === 'needs_reauth' && !googleConnected && (
                <GlassButton variant="primary" size="sm" onClick={reconnect}>
                  <Link2 size={14} /> Reconnect Google
                </GlassButton>
              )}
              {(src.status === 'needs_remap' || src.key_conflict_rows > 0) && (
                <GlassButton variant="secondary" size="sm" disabled={busy} onClick={() => remap(src)}>
                  <RefreshCw size={14} /> Re-map
                </GlassButton>
              )}
              {problemRows > 0 && (
                <GlassButton variant="secondary" size="sm" disabled={busy}
                  onClick={() => act(src, () => gsheetImportApi.retryFailed(src.id), 'Rows queued for the next sync')}>
                  <RotateCcw size={14} /> Retry failed rows
                </GlassButton>
              )}
              {src.last_job_id && (
                <GlassButton variant="secondary" size="sm" onClick={() => openLatestJob(src)}>
                  <ExternalLink size={14} /> Latest import
                </GlassButton>
              )}
              <GlassButton variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmStop(src)}>
                <Trash2 size={14} /> Stop syncing
              </GlassButton>
            </div>
          </div>
        );
      })}

      <GlassModal
        isOpen={!!confirmStop}
        onClose={() => setConfirmStop(null)}
        title="Stop syncing this sheet?"
        size="sm"
        footer={(
          <>
            <GlassButton variant="secondary" onClick={() => setConfirmStop(null)}>Cancel</GlassButton>
            <GlassButton
              variant="danger"
              onClick={() => {
                const src = confirmStop;
                setConfirmStop(null);
                if (src) act(src, () => gsheetImportApi.deleteSource(src.id), 'Stopped syncing');
              }}
            >
              Stop syncing
            </GlassButton>
          </>
        )}
      >
        <p className="crm-import-hint">
          New rows added to the sheet will no longer be imported. Leads already in your CRM stay there, and the
          import history is kept. Adding the tab again later re-imports it: rows with an email update the existing
          lead, rows without one are imported again.
        </p>
      </GlassModal>

      <GlassModal
        isOpen={!!confirmNew}
        onClose={() => setConfirmNew(null)}
        title="Import these as new rows?"
        size="sm"
        footer={(
          <>
            <GlassButton variant="secondary" onClick={() => setConfirmNew(null)}>Cancel</GlassButton>
            <GlassButton
              variant="primary"
              onClick={() => {
                const src = confirmNew;
                setConfirmNew(null);
                if (src) resume(src, true);
              }}
            >
              Yes, import them
            </GlassButton>
          </>
        )}
      >
        <p className="crm-import-hint">
          {confirmNew?.rows_recognised_last != null
            ? `The last sync recognised ${confirmNew.rows_recognised_last} rows that are already in your CRM; most of them are no longer recognised. `
            : ''}
          If you replaced the sheet's rows with new leads, continue. If you only re-formatted the key column (for
          example a long number column), undo that change and click Resume instead — otherwise rows without an email
          are imported a second time.
        </p>
      </GlassModal>
    </div>
  );
};

export default GsheetLeadSourcesList;
